import React, { useState, useEffect } from 'react';
import { Grid, Paper, Typography, Box, Button } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { adminAPI, analyticsAPI } from '../services/api';
import StatsCards from '../components/Dashboard/StatsCards';
import TrafficChart from '../components/Dashboard/TrafficChart';
import TopZipCodes from '../components/Dashboard/TopZipCodes';
import './Dashboard.css';

// console.log('🔍 [Dashboard] LOADING THE SINGLE DASHBOARD - USING CONSOLIDATED ENDPOINT');

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalBookkeepers: 0,
    pendingApprovals: 0,
    expiringSoon: 0,
    totalSearches: 0
  });
  const [trafficData, setTrafficData] = useState([]);
  const [topZipCodes, setTopZipCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      setChartsLoading(true);
      // console.log('🔄 [Dashboard] Fetching from CONSOLIDATED endpoint: /admin/analytics/dashboard-stats');
      
      // USING THE CONSOLIDATED ENDPOINT for main stats
      const response = await adminAPI.getDashboardStats();
      // console.log('📊 [Dashboard] Consolidated API Response:', response);
      
      if (response.data && response.data.success) {
        // Set the main stats from consolidated response
        setStats({
          totalBookkeepers: response.data.data?.totalBookkeepers || 0,
          pendingApprovals: response.data.data?.pendingApprovals || 0,
          expiringSoon: response.data.data?.expiringSoon || 0,
          totalSearches: response.data.data?.totalSearches || 0
        });

        // console.log('✅ [Dashboard] Stats from CONSOLIDATED endpoint:', {
        //   totalBookkeepers: response.data.data?.totalBookkeepers,
        //   pendingApprovals: response.data.data?.pendingApprovals,
        //   expiringSoon: response.data.data?.expiringSoon,
        //   totalSearches: response.data.data?.totalSearches
        // });
      } else {
        console.error('❌ [Dashboard] Consolidated API returned success: false', response);
        // Fallback to individual APIs if consolidated endpoint fails
        await fetchIndividualStats();
      }
    } catch (error) {
      console.error('❌ [Dashboard] Error fetching from consolidated endpoint:', error);
      // Fallback to individual APIs
      await fetchIndividualStats();
    } finally {
      setLoading(false);
    }
  };

  // Fetch traffic and zip code data separately
  const fetchChartsData = async () => {
    try {
      // console.log('📈 [Dashboard] Fetching charts data...');
      
      // Set default period to "month_to_date"
      const params = { period: 'month_to_date' };
      
      const [trafficResponse, zipCodesResponse] = await Promise.all([
        analyticsAPI.getTraffic(params).catch(err => {
          console.warn('⚠️ [Dashboard] Traffic data fetch failed:', err);
          return { data: { success: false, data: { analytics: [] } } };
        }),
        // For top zip codes, we can use the analytics endpoint or a specific zip codes endpoint
        adminAPI.getGeographicAnalytics().catch(err => {
          console.warn('⚠️ [Dashboard] Zip codes data fetch failed:', err);
          return { data: { success: false, data: { topZipCodes: [] } } };
        })
      ]);

      // console.log('📈 [Dashboard] Traffic response:', trafficResponse.data);
      // console.log('📈 [Dashboard] Zip codes response:', zipCodesResponse.data);

      // Set traffic data
      if (trafficResponse.data?.success) {
        setTrafficData(trafficResponse.data.data?.analytics || []);
        // console.log('✅ [Dashboard] Traffic data updated:', trafficResponse.data.data?.analytics?.length || 0, 'records');
      } else {
        // If no traffic data, create some sample data for demonstration
        setTrafficData(generateSampleTrafficData());
        // console.log('📊 [Dashboard] Using sample traffic data');
      }

      // Set top zip codes data
      if (zipCodesResponse.data?.success) {
        setTopZipCodes(zipCodesResponse.data.data?.topZipCodes || []);
        // console.log('✅ [Dashboard] Top zip codes updated:', zipCodesResponse.data.data?.topZipCodes?.length || 0, 'records');
      } else {
        // If no zip code data, create some sample data
        setTopZipCodes(generateSampleZipCodeData());
        // console.log('📊 [Dashboard] Using sample zip code data');
      }

    } catch (error) {
      console.error('❌ [Dashboard] Error fetching charts data:', error);
      // Use sample data as fallback
      setTrafficData(generateSampleTrafficData());
      setTopZipCodes(generateSampleZipCodeData());
    } finally {
      setChartsLoading(false);
    }
  };

  // Generate sample traffic data for demonstration
  const generateSampleTrafficData = () => {
    const days = 15;
    const data = [];
    const today = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      data.push({
        date: date.toISOString().split('T')[0],
        searches: Math.floor(Math.random() * 20) + 5, // Random between 5-25
        dateLabel: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      });
    }
    
    return data;
  };

  // Generate sample zip code data for demonstration
  const generateSampleZipCodeData = () => {
    return [
      { zipCode: '90210', count: 15, state: 'CA' },
      { zipCode: '10001', count: 12, state: 'NY' },
      { zipCode: '60601', count: 10, state: 'IL' },
      { zipCode: '75201', count: 8, state: 'TX' },
      { zipCode: '33101', count: 6, state: 'FL' },
      { zipCode: '90001', count: 5, state: 'CA' },
      { zipCode: '19101', count: 4, state: 'PA' },
      { zipCode: '85001', count: 3, state: 'AZ' }
    ];
  };

  // Fallback method using individual APIs for main stats
  const fetchIndividualStats = async () => {
    try {
      // console.log('🔄 [Dashboard] Falling back to individual APIs for stats...');
      
      const [pendingRes, expiringRes, bookkeepersRes] = await Promise.all([
        adminAPI.getPending().catch(() => ({ data: { bookkeepers: [] } })),
        adminAPI.getExpiringSoon().catch(() => ({ data: { bookkeepers: [] } })),
        adminAPI.getBookkeepers({ page: 1, limit: 1 }).catch(() => ({ data: { total: 0 } }))
      ]);

      const totalBookkeepers = bookkeepersRes.data?.total || 0;
      const pendingApprovals = pendingRes.data?.bookkeepers?.length || 0;
      const expiringSoon = expiringRes.data?.bookkeepers?.length || 0;

      setStats({
        totalBookkeepers,
        pendingApprovals,
        expiringSoon,
        totalSearches: 0
      });

      // console.log('✅ [Dashboard] Fallback stats:', { totalBookkeepers, pendingApprovals, expiringSoon });
    } catch (error) {
      console.error('❌ [Dashboard] Fallback also failed:', error);
    }
  };

  // Refresh all data
  const refreshAllData = async () => {
    await Promise.all([
      fetchDashboardStats(),
      fetchChartsData()
    ]);
  };

  useEffect(() => {
    const loadAllData = async () => {
      await fetchDashboardStats();
      await fetchChartsData();
    };
    
    loadAllData();
  }, []);

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header with Refresh Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Dashboard
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={refreshAllData}
          disabled={loading || chartsLoading}
        >
          {(loading || chartsLoading) ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </Box>
      
      {/* Stats Cards */}
      <StatsCards stats={stats} loading={loading} />
      
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Traffic Chart */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Search Traffic (Month to Date)
            </Typography>
            <TrafficChart data={trafficData} loading={chartsLoading} />
          </Paper>
        </Grid>
        
        {/* Top Zip Codes */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Top Search Zip Codes
            </Typography>
            <TopZipCodes data={topZipCodes} loading={chartsLoading} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;